import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AssignmentIcon from "@mui/icons-material/Assignment";
import TimelineIcon from "@mui/icons-material/Timeline";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AppsIcon from "@mui/icons-material/Apps";

/**
 * Data for the hero use-case picker: short label, icon, and one-line benefit for quick scanning.
 */
const HERO_USE_CASE_CARDS = [
  {
    id: "proposals",
    label: "Proposals & quotes",
    benefit: "Turn discovery calls into tailored proposals.",
    Icon: RequestQuoteIcon,
  },
  {
    id: "sow",
    label: "Statements of work",
    benefit: "Structure SOWs and commercial language from transcripts.",
    Icon: AssignmentIcon,
  },
  {
    id: "timelines",
    label: "Timelines & plans",
    benefit: "Build phased delivery plans tied to the right project.",
    Icon: TimelineIcon,
  },
  {
    id: "sessions",
    label: "Sessions & transcripts",
    benefit: "Record or import meetings; keep context with the deal.",
    Icon: GraphicEqIcon,
  },
  {
    id: "handoffs",
    label: "Sales → delivery handoffs",
    benefit: "Executive readouts and briefs without losing nuance.",
    Icon: SwapHorizIcon,
  },
  {
    id: "more",
    label: "More use cases",
    benefit: "Compliance docs, test plans, and team-specific templates.",
    Icon: AppsIcon,
  },
];

/**
 * Marketing hero: centered headline, benefit-forward use-case cards, and primary CTA on a dark gradient.
 *
 * @param {object} props Component props.
 * @param {() => void} props.onGetStarted Invoked for sign-up flow (e.g. login route) when a use-case card is clicked—except “More use cases”.
 * @param {() => void} props.onWatchDemo Invoked when the user jumps to the embedded walkthrough.
 * @param {() => void} [props.onExploreUseCases] Invoked only for the “More use cases” card (e.g. navigate to /use-cases).
 * @returns {React.ReactElement} Hero section markup.
 */
const LandingHero = ({ onGetStarted, onWatchDemo, onExploreUseCases }) => {
  const [activeCaseId, setActiveCaseId] = useState(HERO_USE_CASE_CARDS[0].id);

  return (
    <Box
      id="hero"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 10, md: 14 },
        pb: { xs: 6, md: 8 },
        background: `radial-gradient(ellipse 90% 70% at 80% -10%, rgba(124, 58, 237, 0.55) 0%, transparent 55%),
          radial-gradient(ellipse 60% 50% at 10% 100%, rgba(59, 130, 246, 0.35) 0%, transparent 50%),
          linear-gradient(165deg, #0b1220 0%, #1e1b4b 42%, #172554 100%)`,
        color: "common.white",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          opacity: 0.35,
          backgroundImage: `repeating-radial-gradient(
            circle at 50% 45%,
            transparent 0,
            transparent 48px,
            rgba(255, 255, 255, 0.04) 48px,
            rgba(255, 255, 255, 0.04) 49px
          )`,
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Stack
          spacing={{ xs: 3, md: 4 }}
          alignItems="center"
          textAlign="center"
        >
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 999,
              backgroundColor: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.4)",
              color: "common.white",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ letterSpacing: 0.3, color: "rgba(255,255,255,0.98)" }}
            >
              B2B workspace · customers, projects & meeting sessions
            </Typography>
          </Paper>

          <Box>
            <Typography
              variant="h2"
              component="h1"
              fontWeight={800}
              sx={{
                fontSize: { xs: "2rem", sm: "2.75rem", md: "3.25rem" },
                lineHeight: 1.15,
                mb: 1,
              }}
            >
              Ship the documents your deals need.
            </Typography>
            <Typography
              variant="h2"
              component="span"
              display="block"
              fontWeight={800}
              sx={{
                fontSize: { xs: "2rem", sm: "2.75rem", md: "3.25rem" },
                lineHeight: 1.15,
                opacity: 0.92,
              }}
            >
              From the meetings you already have.
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              maxWidth: 720,
              mx: "auto",
              fontWeight: 400,
              color: "rgba(255,255,255,0.82)",
              lineHeight: 1.6,
              fontSize: { xs: "1rem", md: "1.125rem" },
            }}
          >
            Link transcripts to the right customer and project, then generate
            and edit rich deliverables with templates—so sales and delivery work
            from the same source of truth.
          </Typography>

          <Box sx={{ width: "100%", maxWidth: 920 }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ mb: 2, color: "rgba(255,255,255,0.95)" }}
            >
              How will your team use QuillAI?
            </Typography>
            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              useFlexGap
              sx={{ gap: 1.5 }}
            >
              {HERO_USE_CASE_CARDS.map(({ id, label, benefit, Icon }) => {
                const selected = activeCaseId === id;
                return (
                  <Paper
                    key={id}
                    component="button"
                    type="button"
                    onClick={() => {
                      setActiveCaseId(id);
                      if (id === "more") {
                        onExploreUseCases?.();
                      } else {
                        onGetStarted?.();
                      }
                    }}
                    elevation={0}
                    sx={{
                      width: { xs: "calc(50% - 8px)", sm: 200, md: 210 },
                      minHeight: 118,
                      p: 1.75,
                      textAlign: "left",
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: selected
                        ? "primary.light"
                        : "rgba(255,255,255,0.2)",
                      backgroundColor: selected
                        ? "rgba(255,255,255,0.14)"
                        : "rgba(255,255,255,0.96)",
                      color: selected ? "common.white" : "text.primary",
                      borderRadius: 2,
                      transition:
                        "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
                      boxShadow: selected
                        ? "0 12px 30px rgba(0,0,0,0.25)"
                        : "0 4px 14px rgba(15,23,42,0.08)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 14px 32px rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                        backgroundColor: selected
                          ? "rgba(255,255,255,0.18)"
                          : "primary.main",
                        color: selected
                          ? "common.white"
                          : "primary.contrastText",
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 0.5, lineHeight: 1.25 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: selected
                          ? "rgba(255,255,255,0.85)"
                          : "text.secondary",
                        lineHeight: 1.35,
                      }}
                    >
                      {benefit}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          </Box>

          <Stack spacing={1.5} alignItems="center" sx={{ pt: 1 }}>
            <Button
              size="large"
              variant="contained"
              color="secondary"
              onClick={onGetStarted}
              sx={{
                px: 4,
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1.05rem",
                borderRadius: 999,
                boxShadow: "0 8px 24px rgba(249, 72, 74, 0.35)",
              }}
            >
              Get started free
            </Button>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              <PlayCircleOutlineIcon
                sx={{ color: "rgba(255,255,255,0.85)", fontSize: 22 }}
              />
              <Typography
                component="button"
                type="button"
                variant="body2"
                onClick={onWatchDemo}
                sx={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.85)",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  font: "inherit",
                }}
              >
                Watch the walkthrough below
              </Typography>
            </Stack>
          </Stack>

          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.65)", maxWidth: 560 }}
          >
            Templates, AI-assisted drafting, and session history—structured so
            every artifact stays traceable to the call.
          </Typography>
        </Stack>
      </Container>

      <Container
        maxWidth="md"
        sx={{ position: "relative", zIndex: 1, pt: { xs: 4, md: 6 } }}
      >
        <Paper
          id="product-video"
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, sm: 3 },
            backgroundColor: "background.paper",
            color: "text.primary",
            boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
            scrollMarginTop: "88px",
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            Product walkthrough
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            See QuillAI in action
          </Typography>
          <Box
            sx={{
              position: "relative",
              borderRadius: 3,
              overflow: "hidden",
              pt: "56.25%",
              backgroundColor: "#000",
            }}
          >
            <Box
              component="iframe"
              src="https://www.youtube.com/embed/yUNOHYkxbtU?si=tFFABI3I1ROu27qo"
              title="QuillAI: Turn meeting transcripts into working documents."
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LandingHero;
